/**
 * Pure model of the Rename / Move dialog (S8 Task 8.10): the draft, the destination it names with live validation
 * (the Task 8.2 path rules — `normalizeHnsPath`, `hnsWireNameProblem`, `isMoveIntoSelf`), and the dialog's sentences.
 * The copy never says "atomic" (NOTES rows M6 / M7): the move is sent once, refused when the destination exists or the
 * source changed, and the dialog reports what the service answered.
 */
import { hnsWireNameProblem, isMoveIntoSelf, normalizeHnsPath } from '../../../../../core/engines/azureStorageHnsPaths';
import type { HnsLocator, HnsPathRef } from '../../../../../types/azureStorage';

export type MoveAction = 'rename' | 'move';
export type MoveDraft = { action: MoveAction; name: string; fileSystem: string; path: string };
export type MoveDestination = { ok: true; destination: HnsLocator } | { ok: false; reason: string };

export const lastSegmentOf = (path: string): string => path.slice(path.lastIndexOf('/') + 1);
export const parentPathOf = (path: string): string => (path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '');
export const hnsLabel = (ref: HnsLocator): string => (ref.path ? `${ref.fileSystem}/${ref.path}` : ref.fileSystem);

export function initialMoveDraft(source: HnsPathRef, action: MoveAction): MoveDraft {
  return { action, name: lastSegmentOf(source.path), fileSystem: source.fileSystem, path: source.path };
}

export function moveDestination(source: HnsPathRef, draft: MoveDraft): MoveDestination {
  if (draft.action === 'rename') {
    if (draft.name === '') return { ok: false, reason: 'Enter a name.' };
    if (draft.name.includes('/')) return { ok: false, reason: 'A name cannot contain "/"; use Move… to put it in another folder.' };
    const problem = draft.name === '.' || draft.name === '..' ? 'A name cannot be "." or "..".' : hnsWireNameProblem(draft.name);
    if (problem) return { ok: false, reason: problem };
    if (draft.name === lastSegmentOf(source.path)) return { ok: false, reason: 'Enter a different name.' };
    const parent = parentPathOf(source.path);
    return { ok: true, destination: { fileSystem: source.fileSystem, path: parent ? `${parent}/${draft.name}` : draft.name } };
  }
  const normalized = normalizeHnsPath(draft.path);
  if (normalized.ok === false) return { ok: false, reason: normalized.reason };
  const destination = { fileSystem: draft.fileSystem, path: normalized.path };
  if (destination.fileSystem === source.fileSystem && destination.path === source.path) return { ok: false, reason: 'Choose a different location.' };
  if (source.isDirectory && isMoveIntoSelf(source, destination)) return { ok: false, reason: `${hnsLabel(source)} cannot be moved into itself.` };
  return { ok: true, destination };
}

export const MOVE_AMBIGUOUS_TEXT = 'Outcome unknown — the list was refreshed; check both locations.';

export const partialMoveText = (source: HnsLocator, destination: HnsLocator): string =>
  `Part of ${lastSegmentOf(source.path)} was moved to ${lastSegmentOf(destination.path)}; the rest is still at ${lastSegmentOf(source.path)}.`;

/** Code review astra-D #4: the F8 guard's reads failed, so Continue sent nothing (the record stays `ready`). */
export const continueRefusedMoveText = (source: HnsLocator, destination: HnsLocator): string =>
  `${partialMoveText(source, destination)} Continue sent nothing: it needs ${hnsLabel(source)} to still be a folder and ${hnsLabel(destination)} to be a folder holding the part already moved, and that could not be confirmed. Check both locations.`;

export const uncertainMoveText = (source: HnsLocator, destination: HnsLocator): string =>
  `Jam SQL cannot tell whether ${hnsLabel(source)} was moved completely. Check ${hnsLabel(source)} and ${hnsLabel(destination)}.`;

/** NOTES row M10 (S8.0 exit change 9): deleted children stay under the OLD path after a directory rename. */
export const softDeleteRenameText = (source: HnsLocator): string => {
  const name = lastSegmentOf(source.path);
  return `Deleted files that were inside ${name} stay listed under the old path ${name}/ and can be restored only after a folder named ${name} exists again.`;
};

export const moveRulesText = (source: HnsPathRef): string =>
  `${source.isDirectory ? 'Everything in the folder moves with it. ' : ''}Jam SQL never overwrites: the move is refused if the destination already exists, or if ${lastSegmentOf(source.path)} changed after it was checked.`;

/** New directory: the directory a typed name (or relative path — missing parents are created, row M9) names under `parent`. */
export type NewDirectoryTarget = { ok: true; target: HnsLocator } | { ok: false; reason: string };

export function newDirectoryTarget(parent: HnsLocator, name: string): NewDirectoryTarget {
  if (name === '') return { ok: false, reason: 'Enter a name.' };
  const normalized = normalizeHnsPath(name);
  if (normalized.ok === false) return { ok: false, reason: normalized.reason };
  if (normalized.path !== name) return { ok: false, reason: 'Remove the leading or trailing "/".' };
  return { ok: true, target: { fileSystem: parent.fileSystem, path: parent.path ? `${parent.path}/${name}` : name } };
}
